import { encode } from '@toon-format/toon';
import type { CostByInitiativeResponse } from '../../api/financials-client/financials-client.type';
import {
  COST_BY_INITIATIVE_SCHEMA,
  type FlatInitiative,
  type FlatInitiativeTeamData,
  type FlatInitiativeTimeSeries,
  type FlattenedCostByInitiativeData,
} from './cost-by-initiative.type';

/**
 * Flattens initiative data into a simplified format for TOON encoding.
 * @param {CostByInitiativeResponse[]} data - The cost-by-initiative data array
 * @returns {FlatInitiative[]} Array of flattened initiative data
 */
const flattenInitiatives = (data: CostByInitiativeResponse[]): FlatInitiative[] => {
  return data.map((initiative) => ({
    i: initiative.id,
    n: initiative.name,
    ctd: initiative.cost_to_date,
  }));
};

/**
 * Flattens initiative-level time series data (date-keyed costs).
 * @param {CostByInitiativeResponse[]} data - The cost-by-initiative data array
 * @returns {FlatInitiativeTimeSeries[]} Array of flattened initiative time series data
 */
const flattenInitiativeTimeSeries = (data: CostByInitiativeResponse[]): FlatInitiativeTimeSeries[] => {
  const timeSeries: FlatInitiativeTimeSeries[] = [];

  for (const initiative of data) {
    if (!initiative.chart_data?.initiative) {
      continue;
    }

    for (const [date, cost] of Object.entries(initiative.chart_data.initiative)) {
      timeSeries.push({
        i: initiative.id,
        d: date,
        c: cost,
      });
    }
  }

  return timeSeries;
};

/**
 * Flattens team-level data from all initiatives.
 * @param {CostByInitiativeResponse[]} data - The cost-by-initiative data array
 * @returns {FlatInitiativeTeamData[]} Array of flattened initiative team data
 */
const flattenInitiativeTeamData = (data: CostByInitiativeResponse[]): FlatInitiativeTeamData[] => {
  const teamData: FlatInitiativeTeamData[] = [];

  for (const initiative of data) {
    if (!initiative.chart_data?.teams) {
      continue;
    }

    for (const team of initiative.chart_data.teams) {
      if (!team.costs) {
        continue;
      }

      for (const [date, cost] of Object.entries(team.costs)) {
        teamData.push({
          i: initiative.id,
          tid: team.id,
          tm: team.name,
          d: date,
          c: cost,
        });
      }
    }
  }

  return teamData;
};

/**
 * Formats the cost-by-initiative data for Flora by transforming the nested structure
 * into a flattened format optimized for TOON encoding.
 *
 * The function:
 * 1. Flattens nested date-keyed objects into uniform arrays
 * 2. Uses abbreviated field names to reduce token count
 * 3. Includes a schema mapping for Flora to interpret the abbreviations
 *
 * @param {CostByInitiativeResponse[] | null} data - The cost-by-initiative data array
 * @returns {string} TOON-encoded string of the flattened data, or empty string if no data
 */
const formatFloraCostByInitiativeData = (data: CostByInitiativeResponse[] | null): string => {
  if (!data || data.length === 0) {
    return '';
  }

  const flattenedData: FlattenedCostByInitiativeData = {
    schema: COST_BY_INITIATIVE_SCHEMA,
    initiatives: flattenInitiatives(data),
    timeSeries: flattenInitiativeTimeSeries(data),
    teamData: flattenInitiativeTeamData(data),
  };

  // Don't encode if there's no meaningful data
  if (flattenedData.initiatives.length === 0) {
    return '';
  }

  return encode(flattenedData);
};

export { formatFloraCostByInitiativeData };
